import React, { useRef, useState } from "react";
import {
  X,
  AlertTriangle,
  Package,
  FileText,
  Loader,
  Upload,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import { isValidPhoneFormat } from "../../utils/phone";
import { driversAPI, driverPublicAPI } from "../../services/api";
import { FormField, Input, Select, Textarea } from "../UI/FormField";

interface Props {
  tokenOrId: string;
  shipmentNumber: string;
  isPublicAccess?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DRIVER_TYPES: {
  value: "damage" | "loss" | "documentation" | "other";
  label: string;
  icon: React.ReactNode;
  desc: string;
}[] = [
  {
    value: "damage",
    label: "Damage",
    icon: <AlertTriangle className="h-5 w-5" />,
    desc: "Goods arrived damaged",
  },
  {
    value: "loss",
    label: "Loss",
    icon: <Package className="h-5 w-5" />,
    desc: "Goods not delivered",
  },
  {
    value: "documentation",
    label: "Documentation",
    icon: <FileText className="h-5 w-5" />,
    desc: "Incorrect or missing docs",
  },
  {
    value: "other",
    label: "Other",
    icon: <AlertTriangle className="h-5 w-5" />,
    desc: "Other issue",
  },
];

type DriverReclamationType = (typeof DRIVER_TYPES)[number]["value"];

interface SubmittedInfo {
  reclamationNumber: string;
}

const DriverReclamationModal: React.FC<Props> = ({
  tokenOrId,
  shipmentNumber,
  isPublicAccess = false,
  onClose,
  onSuccess,
}) => {
  const [type, setType] = useState<DriverReclamationType | "">("");
  const [description, setDescription] = useState("");
  const [incidentDate, setIncidentDate] = useState("");
  const [incidentTime, setIncidentTime] = useState("");
  const [incidentStage, setIncidentStage] = useState("");
  const [incidentStageNote, setIncidentStageNote] = useState("");
  const [claimantEmail, setClaimantEmail] = useState("");
  const [claimantName, setClaimantName] = useState("");
  const [claimantPhone, setClaimantPhone] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SubmittedInfo | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const allowed = selected.filter((f) => f.size <= 10 * 1024 * 1024);
    if (allowed.length < selected.length)
      toast.error("Some files exceeded 10 MB and were skipped");
    setFiles((prev) => [...prev, ...allowed].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (submitted) onSuccess();
    onClose();
  };

  const handleSubmit = async () => {
    if (!type) {
      toast.error("Please select a reclamation type");
      return;
    }
    if (!description.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (isPublicAccess && !emailRe.test(claimantEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!isValidPhoneFormat(claimantPhone)) {
      toast.error(
        "Enter a valid phone number — digits, optionally with +, spaces, brackets or dashes",
      );
      return;
    }

    const formData = new FormData();
    formData.append("type", type);
    formData.append("description", description.trim());
    if (incidentDate) formData.append("incidentDate", incidentDate);
    if (incidentTime) formData.append("incidentTime", incidentTime);
    if (incidentStage) formData.append("incidentStage", incidentStage);
    if (incidentStage === "other" && incidentStageNote.trim())
      formData.append("incidentStageNote", incidentStageNote.trim());
    if (isPublicAccess) {
      formData.append("claimantEmail", claimantEmail.trim().toLowerCase());
      if (claimantName.trim())
        formData.append("claimantName", claimantName.trim());
      if (claimantPhone.trim())
        formData.append("claimantPhone", claimantPhone.trim());
    }
    files.forEach((f) => formData.append("attachments", f));

    try {
      setSubmitting(true);
      const api = isPublicAccess ? driverPublicAPI : driversAPI;
      const res = await api.fileReclamation(tokenOrId, formData);
      setSubmitted({
        reclamationNumber: res.data.data.reclamationNumber,
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(msg ?? "Failed to submit reclamation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              File a Reclamation
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Shipment {shipmentNumber}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {submitted ? (
            /* ── Success screen ── */
            <div className="text-center py-8 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Reclamation Submitted
                </h3>
                <p className="text-gray-500 mt-1">Your reference number is</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {submitted.reclamationNumber}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Type selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Reclamation Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {DRIVER_TYPES.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={`flex flex-col items-start p-3 rounded-xl border-2 text-left transition-all ${
                        type === opt.value
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            type === opt.value
                              ? "text-blue-600"
                              : "text-gray-500"
                          }
                        >
                          {opt.icon}
                        </span>
                        <span className="font-medium text-sm text-gray-900">
                          {opt.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 mt-0.5">
                        {opt.desc}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Incident date / time / stage */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Incident Details
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <FormField size="sm" label="Date">
                    <Input
                      type="date"
                      size="sm"
                      value={incidentDate}
                      onChange={(e) => setIncidentDate(e.target.value)}
                    />
                  </FormField>
                  <FormField size="sm" label="Time">
                    <Input
                      type="time"
                      size="sm"
                      value={incidentTime}
                      onChange={(e) => setIncidentTime(e.target.value)}
                    />
                  </FormField>
                  <FormField size="sm" label="Stage">
                    <Select
                      size="sm"
                      value={incidentStage}
                      onChange={(e) => {
                        setIncidentStage(e.target.value);
                        setIncidentStageNote("");
                      }}
                    >
                      <option value="">Select…</option>
                      <option value="loading">Loading</option>
                      <option value="unloading">Unloading</option>
                      <option value="in_transit">In Transit</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormField>
                </div>
                {incidentStage === "other" && (
                  <FormField size="sm" label="Specify stage">
                    <Textarea
                      size="sm"
                      value={incidentStageNote}
                      onChange={(e) => setIncidentStageNote(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder="Please specify…"
                    />
                  </FormField>
                )}
              </div>

              {/* Contact info (public link access only) */}
              {isPublicAccess && (
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700">
                    Your Contact Details
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormField size="sm" label="Email" required>
                      <Input
                        type="email"
                        size="sm"
                        value={claimantEmail}
                        onChange={(e) => setClaimantEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </FormField>
                    <FormField size="sm" label="Full Name">
                      <Input
                        type="text"
                        size="sm"
                        value={claimantName}
                        onChange={(e) => setClaimantName(e.target.value)}
                        placeholder="Your name"
                      />
                    </FormField>
                    <div className="sm:col-span-2">
                      <FormField size="sm" label="Phone">
                        <Input
                          type="tel"
                          size="sm"
                          value={claimantPhone}
                          onChange={(e) => setClaimantPhone(e.target.value)}
                          placeholder="+1 234 567 8900"
                        />
                      </FormField>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <FormField
                size="sm"
                label="Description"
                required
                hint={`${description.length} / 3000`}
              >
                <Textarea
                  size="sm"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={3000}
                  placeholder="Describe the issue in detail — what happened, when, what was affected..."
                />
              </FormField>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Supporting Documents{" "}
                  <span className="text-gray-400 font-normal">
                    (max 5 files, 10 MB each)
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileAdd}
                  className="hidden"
                />
                {files.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors w-full justify-center"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Add photos or documents</span>
                  </button>
                )}
                {files.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {files.map((f, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <span className="text-sm text-gray-700 truncate flex-1">
                          {f.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFiles((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            )
                          }
                          className="ml-2 p-1 hover:text-red-600 text-gray-400 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 shrink-0">
          {submitted ? (
            <button
              onClick={handleClose}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center space-x-2 px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {submitting && <Loader className="h-4 w-4 animate-spin" />}
                <span>{submitting ? "Submitting…" : "Submit Reclamation"}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverReclamationModal;
